import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOnboarding } from '../context/OnboardingContext';
import InteractiveQuestionCard from '../components/enhanced-onboarding/InteractiveQuestionCard';
import ProgressRing from '../components/enhanced-onboarding/ProgressRing';
import OnboardingChatBubble from '../components/enhanced-onboarding/OnboardingChatBubble';
import PersonalityInsights from '../components/enhanced-onboarding/PersonalityInsights';
import '../styles/onboarding-page.css';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { user } = useAuth();
  const {
    currentSession,
    isOnboardingActive,
    hasCompletedOnboarding,
    loading,
    error,
    submitResponse,
    startOnboarding,
    cancelOnboarding,
    clearError
  } = useOnboarding();

  // Enhanced state management
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState([]);
  const [showCompletion, setShowCompletion] = useState(false);
  const [completedProfile, setCompletedProfile] = useState(null);
  const [questionHistory, setQuestionHistory] = useState([]);
  const [userScore, setUserScore] = useState(0);
  const [streakCount, setStreakCount] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [currentQuestionType, setCurrentQuestionType] = useState('chat');

  const containerRef = useRef(null);
  const celebrationRef = useRef(null);

  // Enhanced question types and flow logic
  const QUESTION_TYPES = {
    CHAT: 'chat',           // Conversational chat bubble
    CARD_SELECT: 'card_select',    // Visual card selection
    SLIDER: 'slider',       // Risk tolerance slider
    ICON_GRID: 'icon_grid', // Grid of icon options
    DRAG_DROP: 'drag_drop', // Drag and drop ranking
    MULTI_CHOICE: 'multi_choice',  // Enhanced multiple choice
    VISUAL_SCALE: 'visual_scale'   // Visual rating scale
  };

  // Total number of questions for clear progress tracking
  const TOTAL_QUESTIONS = 8;

  // Predefined question templates for better UX
  const enhancedQuestionTemplates = [
    {
      step: 0,
      type: QUESTION_TYPES.CHAT,
      gideonMessage: "Hey there! 👋 I'm Gideon, your investment buddy! Ready to discover your investing superpower?",
      expectsResponse: false,
      nextButtonText: "Let's do this! 🚀"
    },
    {
      step: 1,
      type: QUESTION_TYPES.CARD_SELECT,
      gideonMessage: "If you found $100 on the ground, what would you do?",
      options: [
        {
          id: 'save',
          title: 'Save it all',
          subtitle: 'Keep it safe for later',
          icon: '🏦',
          color: '#10B981',
          points: 10
        },
        {
          id: 'spend_some',
          title: 'Save most, spend a little',
          subtitle: 'Balance is key',
          icon: '⚖️',
          color: '#3B82F6',
          points: 15
        },
        {
          id: 'invest',
          title: 'Invest it to grow',
          subtitle: 'Make money work for me',
          icon: '📈',
          color: '#8B5CF6',
          points: 20
        },
        {
          id: 'spend',
          title: 'Buy something fun',
          subtitle: 'Enjoy life now',
          icon: '🎮',
          color: '#F59E0B',
          points: 5
        }
      ]
    },
    {
      step: 2,
      type: QUESTION_TYPES.VISUAL_SCALE,
      gideonMessage: "Imagine you're playing a treasure hunt game. How do you feel about big risks for big rewards?",
      question: "Risk vs Reward Preference",
      leftLabel: "Small steady treasures",
      rightLabel: "One BIG treasure chest",
      leftIcon: "🪙🪙🪙",
      rightIcon: "💎",
      minValue: 0,
      maxValue: 100,
      defaultValue: 50
    },
    {
      step: 3,
      type: QUESTION_TYPES.ICON_GRID,
      gideonMessage: "What companies do you know and like?",
      question: "Pick your favorite companies (select 3-5):",
      options: [
        { id: 'apple', name: 'Apple', icon: '🍎', ticker: 'AAPL' },
        { id: 'disney', name: 'Disney', icon: '🏰', ticker: 'DIS' },
        { id: 'nike', name: 'Nike', icon: '👟', ticker: 'NKE' },
        { id: 'mcdonalds', name: 'McDonald\'s', icon: '🍟', ticker: 'MCD' },
        { id: 'tesla', name: 'Tesla', icon: '🚗', ticker: 'TSLA' },
        { id: 'netflix', name: 'Netflix', icon: '📺', ticker: 'NFLX' },
        { id: 'microsoft', name: 'Microsoft', icon: '💻', ticker: 'MSFT' },
        { id: 'cocacola', name: 'Coca-Cola', icon: '🥤', ticker: 'KO' }
      ],
      minSelections: 3,
      maxSelections: 5
    },
    {
      step: 4,
      type: QUESTION_TYPES.SLIDER,
      gideonMessage: "When learning something new, what's your style?",
      question: "Learning Pace Preference",
      leftLabel: "Step by step",
      rightLabel: "Show me everything",
      leftIcon: "🐢",
      rightIcon: "🐰",
      minValue: 0,
      maxValue: 100,
      defaultValue: 30,
      marks: [
        { value: 0, label: 'Very detailed' },
        { value: 25, label: 'Step by step' },
        { value: 50, label: 'Balanced' },
        { value: 75, label: 'Quick overview' },
        { value: 100, label: 'Everything at once' }
      ]
    },
    {
      step: 5,
      type: QUESTION_TYPES.DRAG_DROP,
      gideonMessage: "Let's see what motivates you most! Drag these in order of importance:",
      question: "What's most important to you? (Drag to rank)",
      items: [
        { id: 'family', text: 'Help my family', icon: '👨‍👩‍👧‍👦', color: '#EF4444' },
        { id: 'future', text: 'Secure my future', icon: '🌟', color: '#10B981' },
        { id: 'learn', text: 'Learn about money', icon: '🧠', color: '#3B82F6' },
        { id: 'fun', text: 'Have fun with friends', icon: '🎉', color: '#F59E0B' },
        { id: 'success', text: 'Become successful', icon: '🏆', color: '#8B5CF6' }
      ]
    },
    {
      step: 6,
      type: QUESTION_TYPES.CHAT,
      gideonMessage: "Amazing! 🎉 I'm getting to know you better. Just one more quick question...",
      expectsResponse: false,
      nextButtonText: "What's next? 😊"
    },
    {
      step: 7,
      type: QUESTION_TYPES.MULTI_CHOICE,
      gideonMessage: "If the stock market goes down 10% tomorrow, how would you feel?",
      options: [
        {
          id: 'worried',
          text: 'Worried - I\'d want to sell everything',
          emoji: '😰',
          riskLevel: 'conservative',
          points: 5
        },
        {
          id: 'concerned',
          text: 'Concerned but I\'d wait and see',
          emoji: '😟',
          riskLevel: 'moderate-conservative',
          points: 10
        },
        {
          id: 'neutral',
          text: 'It happens - markets go up and down',
          emoji: '😐',
          riskLevel: 'moderate',
          points: 15
        },
        {
          id: 'opportunity',
          text: 'Excited - time to buy more at lower prices!',
          emoji: '😍',
          riskLevel: 'aggressive',
          points: 20
        }
      ]
    }
  ];

  // Initialize state on mount
  useEffect(() => {
    const initializeOnboarding = async () => {
      // Reset local state
      setCurrentStep(0);
      setResponses([]);
      setQuestionHistory([]);
      setUserScore(0);
      setStreakCount(0);
      setShowCompletion(false);
      setCompletedProfile(null);
      setShowCelebration(false);
      setCurrentQuestionType('chat');
      clearError();

      // Initialize backend session if not already active
      if (!isOnboardingActive && !hasCompletedOnboarding) {
        try {
          console.log('OnboardingPage: Initializing onboarding session...');
          await startOnboarding();
        } catch (error) {
          console.error('OnboardingPage: Failed to initialize session:', error);
          // Don't block the UI - local flow can continue
        }
      }
    };

    initializeOnboarding();
  }, [clearError, isOnboardingActive, hasCompletedOnboarding, startOnboarding]);

  // Handle question response
  const handleQuestionResponse = async (responseData) => {
    const currentTemplate = enhancedQuestionTemplates[currentStep];

    try {
      // Add to response history
      const newResponse = {
        step: currentStep,
        type: currentTemplate.type,
        question: currentTemplate.gideonMessage,
        response: responseData,
        timestamp: Date.now(),
        points: responseData.points || 10
      };

      setResponses(prev => [...prev, newResponse]);
      setQuestionHistory(prev => [...prev, newResponse]);

      // Update score and streak
      setUserScore(prev => prev + (responseData.points || 10));
      setStreakCount(prev => prev + 1);

      // Show celebration for good responses
      if (responseData.points >= 15) {
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 1500);
      }

      // Submit to backend
      const formattedResponse = formatResponseForBackend(responseData, currentTemplate);
      const result = await submitResponse(formattedResponse);

      // Move to next step or complete
      if (currentStep >= enhancedQuestionTemplates.length - 1 || result.completed) {
        // Assessment completed!
        setShowCompletion(true);
        const finalProfile = result.personalityProfile || generateLocalProfile();
        setCompletedProfile(finalProfile);

        // Always redirect to Dashboard after 2s celebration
        setTimeout(() => {
          navigate('/dashboard', {
            state: {
              onboardingCompleted: true,
              profile: finalProfile
            }
          });
        }, 2000);
      } else {
        // Move to next question with smooth transition
        const nextStep = currentStep + 1;

        // Bounds check: ensure we don't go beyond the last question
        if (nextStep < enhancedQuestionTemplates.length) {
          setTimeout(() => {
            setCurrentStep(nextStep);
            setCurrentQuestionType(enhancedQuestionTemplates[nextStep]?.type || 'chat');
          }, 800);
        } else {
          // Edge case: reached end without completion signal
          console.warn('OnboardingPage: Reached end of questions without completion signal');
          setShowCompletion(true);
          const finalProfile = generateLocalProfile();
          setCompletedProfile(finalProfile);

          setTimeout(() => {
            navigate('/dashboard', {
              state: {
                onboardingCompleted: true,
                profile: finalProfile
              }
            });
          }, 2000);
        }
      }

    } catch (error) {
      console.error('Error submitting response:', error);
      // Don't block progression for API errors - continue with local flow
      if (currentStep < enhancedQuestionTemplates.length - 1) {
        setTimeout(() => {
          setCurrentStep(prev => prev + 1);
          setCurrentQuestionType(enhancedQuestionTemplates[currentStep + 1]?.type || 'chat');
        }, 800);
      }
    }
  };

  // Format response for backend API
  const formatResponseForBackend = (responseData, template) => {
    switch (template.type) {
      case QUESTION_TYPES.CARD_SELECT:
        return `Selected: ${responseData.title} - ${responseData.subtitle}`;
      case QUESTION_TYPES.VISUAL_SCALE:
      case QUESTION_TYPES.SLIDER:
        return `Scale response: ${responseData.value}/100 (${responseData.label || ''})`;
      case QUESTION_TYPES.ICON_GRID:
        return `Selected companies: ${responseData.selections.map(s => s.name).join(', ')}`;
      case QUESTION_TYPES.DRAG_DROP:
        return `Priority ranking: ${responseData.rankedItems.map((item, index) => `${index + 1}. ${item.text}`).join(', ')}`;
      case QUESTION_TYPES.MULTI_CHOICE:
        return `${responseData.text} (Risk level: ${responseData.riskLevel})`;
      default:
        return responseData.text || responseData.toString();
    }
  };

  // Generate local personality profile as fallback
  const generateLocalProfile = () => {
    const riskResponses = responses.filter(r => r.response.riskLevel || r.response.value !== undefined);
    const avgRisk = riskResponses.length > 0
      ? riskResponses.reduce((sum, r) => sum + (r.response.value || r.response.points || 50), 0) / riskResponses.length
      : 50;

    return {
      riskTolerance: {
        score: avgRisk,
        category: avgRisk < 30 ? 'conservative' : avgRisk < 70 ? 'moderate' : 'aggressive',
        confidence: 0.8
      },
      learningStyle: {
        pacePreference: avgRisk < 40 ? 'step-by-step' : avgRisk < 70 ? 'steady' : 'fast',
        visualLearner: true,
        needsEncouragement: avgRisk < 50
      },
      behavioralTraits: {
        patience: Math.min(100 - avgRisk + 20, 100),
        curiosity: userScore / responses.length * 10,
        confidence: avgRisk,
        independence: Math.max(avgRisk - 10, 30)
      }
    };
  };

  const handleClose = () => {
    if (showCompletion) {
      // If completed, go to dashboard
      navigate('/dashboard', {
        state: { onboardingCompleted: true }
      });
    } else {
      // Mid-assessment - ask for confirmation
      if (window.confirm('Exit onboarding?\n\nYour answers will NOT be saved and you\'ll need to start over next time.\n\nAre you sure you want to leave?')) {
        cancelOnboarding();
        navigate('/dashboard');
      }
    }
  };

  const currentTemplate = enhancedQuestionTemplates[Math.min(currentStep, enhancedQuestionTemplates.length - 1)];
  const progress = (Math.min(currentStep, enhancedQuestionTemplates.length - 1) / (enhancedQuestionTemplates.length - 1)) * 100;

  // Guard clause: if template is undefined, show error state
  if (!currentTemplate && !showCompletion) {
    return (
      <div className="onboarding-page">
        <div className="onboarding-content">
          <div className="onboarding-header">
            <div className="header-center">
              <div className="gideon-avatar">🎯</div>
              <div className="header-title">Something went wrong</div>
            </div>
            <button
              className="onboarding-close-btn"
              onClick={() => navigate('/dashboard')}
              aria-label="Return to dashboard"
              style={{
                position: 'absolute',
                right: '20px',
                top: '20px'
              }}
            >
              ×
            </button>
          </div>
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <span className="error-text">
              Unable to load onboarding questions. Please try again.
            </span>
          </div>
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <button
              className="next-button-enhanced"
              onClick={() => navigate('/dashboard')}
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-page">
      <div className="onboarding-content" ref={containerRef}>

        {/* Header with progress and score */}
        <div className="onboarding-header">
          <div className="header-left">
            <div className="progress-indicator">
              <ProgressRing
                progress={progress}
                size={40}
                strokeWidth={3}
                color="#d72638"
              />
              <span className="progress-text">
                {currentStep + 1}/{enhancedQuestionTemplates.length}
              </span>
            </div>
          </div>

          <div className="header-center">
            <div className="gideon-avatar">🎯</div>
            <div className="header-title">Getting to know you!</div>
            <div className="header-subtitle">{TOTAL_QUESTIONS} quick questions</div>
          </div>

          <div className="header-right">
            <div className="score-display">
              <span className="score-icon">⭐</span>
              <span className="score-value">{userScore}</span>
            </div>
            {streakCount > 2 && (
              <div className="streak-display">
                <span className="streak-icon">🔥</span>
                <span className="streak-value">{streakCount}</span>
              </div>
            )}
            {/* Close button */}
            <button
              className="onboarding-close-btn"
              onClick={handleClose}
              disabled={loading}
              aria-label="Exit onboarding (progress will not be saved)"
              title="Exit (progress will not be saved)"
            >
              ×
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <span className="error-text">{error}</span>
            <button onClick={clearError} className="error-dismiss">×</button>
          </div>
        )}

        {/* Main Content */}
        <div className="question-container">

          {/* Completion Screen */}
          {showCompletion && completedProfile ? (
            <PersonalityInsights
              profile={completedProfile}
              totalScore={userScore}
              streak={streakCount}
              responses={responses}
              onContinue={() => {
                navigate('/dashboard', {
                  state: { onboardingCompleted: true }
                });
              }}
            />
          ) : (
            <>
              {/* Current Question */}
              <div className="question-section">

                {/* Gideon's Message */}
                <OnboardingChatBubble
                  message={currentTemplate.gideonMessage}
                  avatar="🎯"
                  typing={loading}
                  delay={300}
                />

                {/* Interactive Question Component */}
                <div className="interactive-question-wrapper">
                  <InteractiveQuestionCard
                    questionData={currentTemplate}
                    onResponse={handleQuestionResponse}
                    loading={loading}
                    currentStep={currentStep}
                    totalSteps={enhancedQuestionTemplates.length}
                  />
                </div>

                {/* Real-time Insights */}
                {responses.length > 2 && (
                  <div className="live-insights">
                    <div className="insights-title">Learning about you... 🤔</div>
                    <div className="insight-pills">
                      {userScore > 50 && <span className="insight-pill">🚀 Go-getter</span>}
                      {responses.some(r => r.response.riskLevel === 'conservative') &&
                        <span className="insight-pill">🛡️ Careful planner</span>}
                      {streakCount > 3 && <span className="insight-pill">🔥 On a roll!</span>}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Celebration Animation */}
        {showCelebration && (
          <div className="celebration-overlay" ref={celebrationRef}>
            <div className="celebration-animation">
              <div className="celebration-emoji">🎉</div>
              <div className="celebration-text">Great choice!</div>
              <div className="celebration-points">+{responses[responses.length - 1]?.points || 10} points</div>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {loading && (
          <div className="loading-overlay">
            <div className="loading-content">
              <div className="loading-gideon">🎯</div>
              <div className="loading-text">Gideon is thinking...</div>
              <div className="loading-animation">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default OnboardingPage;
